"use client";

import React, { useState, useRef } from "react";
import GoogleLoginButton from "@components/SocialAuth/GoogleLoginButton";
import { Post } from "src/services/ClientSideAPIService";
import styles from "./signup.module.css";
import Sites from "src/constants/Sites";

const Signup = ({
  siteId,
  li_did,
  errorMessage,
  showError,
  orderedRefs = {},
  handleSuccess,
  handleError,
}) => {
  const [email, setEmail] = useState("");
  const [googleAuthInProgress, setGoogleAuthInProgress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [displayError, setDisplayError] = useState(false);

  const emailRef = useRef();

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const collectSelectedNewsletters = () => {
    if (!orderedRefs || Object.keys(orderedRefs).length === 0) {
      return [];
    }

    return Object.values(orderedRefs)
      .map((ref) => (ref?.current?.getValue ? ref.current.getValue() : null))
      .filter((val) => val !== null && val !== undefined);
  };

  const handleFormSubmitAsync = async (
    overrideEmail,
    name,
    provider,
    providerId,
    isGoogle = false
  ) => {
    const newsletters = collectSelectedNewsletters();
    const emailValue = (overrideEmail || email || "").trim();

    if (!isValidEmail(emailValue)) {
      setDisplayError(true);
      setErrorMsg("Please provide valid email address");
      if (isGoogle) setGoogleAuthInProgress(false);
      return;
    }

    if (orderedRefs && Object.keys(orderedRefs).length > 0 && !newsletters.length) {
      setDisplayError(true);
      setErrorMsg("Please select at least one newsletter");
      if (isGoogle) setGoogleAuthInProgress(false);
      return;
    }

    if (isGoogle) {
      setGoogleAuthInProgress(true);
    } else {
      setLoading(true);
    }

    setDisplayError(false);
    setErrorMsg("");

    const signupParams = {
      email: emailValue,
      promoPage: true,
      li_did: li_did || "",
    };

    if (orderedRefs && Object.keys(orderedRefs).length > 0) {
      signupParams.newsletters = newsletters;
    }

    // Add provider info (social signup)
    if (provider && providerId) {
      signupParams.provider = provider;
      signupParams.providerId = providerId;

      // Split name into First_Name / Last_Name
      if (name) {
        const [firstName, ...rest] = name.trim().split(" ");
        signupParams.First_Name = firstName;
        signupParams.Last_Name = rest.length > 0 ? rest.join(" ") : "";
      }
    }

    await Post(
      "SignUp",
      { data: signupParams, siteId },
      (res) => {
        setLoading(false);
        setGoogleAuthInProgress(false);
        handleSuccess?.(res);
      },
      (err) => {
        setLoading(false);
        setGoogleAuthInProgress(false);
        setDisplayError(true);
        setErrorMsg("Signup failed. Please try again.");
        handleError?.(err);
      }
    );
  };

  return (
    <div className={styles.right}>
      {displayError || showError ? (
        <div className={styles.errorMsg}>{errorMsg || errorMessage}</div>
      ) : null}

      {siteId === Sites.EARLYBIRDBOOKS && (
        <>
          {" "}
          <GoogleLoginButton
            loading={googleAuthInProgress}
            onSuccess={({ email, name, provider, providerId }) =>
              handleFormSubmitAsync(email, name, provider, providerId, true)
            }
            onError={(msg) => {
              if (msg === "access_denied") {
                setErrorMsg("");
              } else {
                setDisplayError(true);
                setErrorMsg(msg);
              }
              setGoogleAuthInProgress(false);
            }}
            loadingText="Subscribing..."
            buttonText="Continue with Google"
          />
          <div className={styles.divider}>OR</div>
        </>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleFormSubmitAsync();
        }}
      >
        <div className={styles["form-group"]}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            placeholder="you@example.com"
            ref={emailRef}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <button
          type="submit"
          className={styles["submit-btn"]}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Subscribing..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default Signup;
